import { Button, Icon, Input, Select, SelectOptionType, TextArea } from "@aidkitorg/component-library";
import { IconName } from "@aidkitorg/component-library/src/Icon";
import React, { SetStateAction, useState } from "react";
import { useDashboards } from "../../../Contexts/DashboardsContext";
import { HeroNumberWithFields, SummaryExpr } from "@aidkitorg/types/lib/survey";

type DashboardComponent = {
  key: 'metric' | 'bar_chart' | 'pie_chart' | 'map' | 'report',
  name: string,
  subtitle: string,
  icon: IconName,
  onClick: React.MouseEventHandler<HTMLButtonElement> 
}

type DashboardComponentOptionsProps = {
  options: Array<DashboardComponent>
}

const DashboardComponentOptions: React.FC<DashboardComponentOptionsProps> = ({ options }) => {
  return (
    <>
      {options.map((component, idx) => (
        <div key={idx}>
          <Button 
            className="border !w-full !justify-start"
            variant="secondary"
            onClick={component.onClick}
          >
            <div className="flex items-center gap-x-2">
              <div>
                <Icon name={component.icon} role="decorative"/>
              </div>
              <div className="text-left">
                <div>
                  {component.name}
                </div>
                <div className="text-gray-700 font-normal">
                  {component.subtitle}
                </div>
              </div>
            </div>
          </Button>
        </div>
      ))}
    </>
  )
}

type CreateComponentProps = {
  setDisplayMode: React.Dispatch<SetStateAction<string>>
}

const MetricComponent: React.FC<CreateComponentProps> = ({ setDisplayMode }) => {
  const [title, setTitle] = useState('')
  const [metricType, setMetricType] = useState('numeric')
  const [aggregation, setAggregation] = useState('Count')
  const [dataField, setDataField] = useState('')
  const [description, setDescription] = useState('')

  const { 
    setStagedComponents, 
    setStagedComponentsWithResults, 
    getDashboardComponentTranslation 
  } = useDashboards()

  const METRIC_TYPE_OPTIONS: Array<SelectOptionType> = [
    {value: 'numeric', label: 'Numeric'},
    {value: 'percentage', label: 'Percentage'},
    {value: 'currency', label: 'Currency'},
    {value: 'duration', label: 'Duration'}
  ]

  const getMetricTypeOption = (value: string) => METRIC_TYPE_OPTIONS.find((v) => v.value === value)

  const AGGREGATION_OPTIONS: Array<SelectOptionType> = [
    {value: 'Count', label: 'Count'},
    {value: 'Sum', label: 'Sum'},
    {value: 'Mean', label: 'Average'},
    // TODO: add minimum, maximum, we don't have those in distro yet
  ]

  const getAggregationOption = (value: string) => AGGREGATION_OPTIONS.find((v) => v.value === value)

  const saveMetricComponent = async () => {
    const summary = (): SummaryExpr => {
      switch (aggregation) {
        case 'Count': 
          return { kind: 'Count' }
        case 'Sum':
          return {
            kind: 'Sum',
            value: {
              kind: 'Field',
              field: dataField
            }
          }
        case 'Mean':
          return {
            kind: 'Mean',
            value: {
              kind: 'Field', 
              field: dataField
            }
          }
      }

      return { kind: 'Count' }
    }

    const metricToStage: HeroNumberWithFields = {
      kind: 'Hero Number',
      title: title,
      summary: summary(),
      useCommas: true,
    }

    switch (metricType) {
      case 'percentage':
        metricToStage.suffix = '%'
        break;
      case 'currency':
        metricToStage.prefix= '$'
        metricToStage.decimalPlaces = 2
      // TODO: handle duration
    }

    console.log(metricType)
    console.log(metricToStage)
    const results = await getDashboardComponentTranslation(metricToStage)

    const componentWithResultsToStage = {
      ...metricToStage,
      ...results
    }

    setStagedComponents((prevState) => ([...prevState, metricToStage]))
    setStagedComponentsWithResults((prevState: Array<any>) => ([...prevState, componentWithResultsToStage]))
  }
  
  // TODO: polish to make sure all inputs are uniform visually
  return (
    <div className="flex flex-col gap-y-2">
      <div className="font-medium text-lg">
        Add Metric
      </div>
      <Input
        label="Title"
        required
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <Select
        // TODO: add required
        options={METRIC_TYPE_OPTIONS}
        label="Metric Type"
        description="Calculation done for this metric"
        labelLocation="above"
        value={getMetricTypeOption(metricType)}
        onChange={(value) => setMetricType(value.value)}
      />
      <Select
        // TODO: add required
        options={AGGREGATION_OPTIONS}
        label="Aggregation"
        description="Aggregation done for this metric"
        labelLocation="above"
        value={getAggregationOption(aggregation)}
        onChange={(value) => setAggregation(value.value)}
      />
      {aggregation !== 'Count' && (
        <Input
          label="Data Field"
          required
          value={dataField}
          onChange={(e) => setDataField(e.target.value)}
        />
      )}
      <TextArea
        label="Description"
        showLabel
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <div className="flex justify-between gap-x-2 pt-2">
        <Button onClick={() => setDisplayMode('create')} className="!w-1/2" variant="secondary">Cancel</Button>
        <Button onClick={saveMetricComponent} className="!w-1/2">Save</Button>
      </div>
    </div>
  )
}

export const CreateDashboardComponentTab = () => {
  const [displayMode, setDisplayMode] = useState<DashboardComponent['key'] | 'create'>('create')

  const DASHBOARD_COMPONENT_OPTIONS: Array<DashboardComponent> = [
    {
      key: 'metric',
      name: 'Metric',
      subtitle: 'Display a single number or KPI',
      icon: 'CurrencyDollarIcon',
      onClick: () => setDisplayMode('metric')
    },
    {
      key: 'bar_chart',
      name: 'Bar Chart',
      subtitle: 'Compare values across categories',
      icon: 'ChartBarIcon',
      onClick: () => setDisplayMode('bar_chart')
    },
    {
      key: 'pie_chart',
      name: 'Pie Chart',
      subtitle: 'Show composition of a whole',
      icon: 'ChartPieIcon',
      onClick: () => setDisplayMode('pie_chart')
    },
    {
      key: 'map',
      name: 'Map',
      subtitle: 'Visualize geographic data',
      icon: 'MapIcon',
      onClick: () => setDisplayMode('map')
    },
    {
      key: 'report',
      name: 'Add Report',
      subtitle: 'Create a detailed data table',
      icon: 'TableCellsIcon',
      onClick: () => setDisplayMode('report')
    }
  ]

  return (
    <div className="flex flex-col gap-y-2 p-2"> 
      {displayMode === 'create' && <DashboardComponentOptions options={DASHBOARD_COMPONENT_OPTIONS} />}
      {displayMode === 'metric' && <MetricComponent setDisplayMode={() => setDisplayMode('create')} />}
    </div>
  )
}